import { useState, useEffect } from 'react';
import { api } from '../services/api';
import type { InventoryItem } from '../types';

export function InventoryPage() {
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  useEffect(() => {
    loadInventory();
  }, []);

  const loadInventory = async () => {
    setIsLoading(true);
    try {
      const data = await api.getInventory();
      setInventory(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load inventory');
    } finally {
      setIsLoading(false);
    }
  };

  const categories = ['all', ...new Set(inventory.map((item) => item.category))];

  const filteredInventory = inventory.filter((item) => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const getStockStatus = (item: InventoryItem) => {
    if (item.currentQuantity <= item.minStockLevel) return 'low';
    if (item.currentQuantity >= item.maxStockLevel) return 'high';
    return 'normal';
  };

  if (isLoading) {
    return (
      <div className="inventory-page">
        <div className="loading">Loading inventory...</div>
      </div>
    );
  }

  return (
    <div className="inventory-page">
      <h1>Inventory</h1>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="inventory-controls">
        <input
          type="search"
          placeholder="Search items..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="category-filter"
        >
          {categories.map((category) => (
            <option key={category} value={category}>
              {category === 'all' ? 'All Categories' : category}
            </option>
          ))}
        </select>
        <button onClick={loadInventory} className="refresh-btn">
          Refresh
        </button>
      </div>

      {filteredInventory.length === 0 ? (
        <div className="empty-state">
          <p>No inventory items found.</p>
          <p>Upload invoices or receipts to start tracking your inventory.</p>
        </div>
      ) : (
        <div className="inventory-grid">
          {filteredInventory.map((item) => (
            <div key={item.id} className={`inventory-card ${getStockStatus(item)}`}>
              <div className="card-header">
                <h3>{item.name}</h3>
                <span className="category-badge">{item.category}</span>
              </div>
              <div className="card-body">
                <div className="quantity">
                  <span className="value">{item.currentQuantity}</span>
                  <span className="unit">{item.unit}</span>
                </div>
                <div className="stock-levels">
                  <span>Min: {item.minStockLevel}</span>
                  <span>Max: {item.maxStockLevel}</span>
                </div>
              </div>
              <div className="card-footer">
                <span className="last-updated">
                  Updated: {new Date(item.lastUpdated).toLocaleDateString()}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
